import {
  CAPTURED_EXCEPTION_MESSAGE,
  type CaptureMessageLevel,
  type CaptureRecord,
  type CaptureSink,
  captureToLogMeta,
  splitCaptureArgs,
} from "./capture";
import type { TagValue } from "@/packages/o11y/metrics/tags";

export type LogLevel = "debug" | "info" | "warn" | "error";

/** Per-call fields. Prefer scalars. */
export type LogMeta = Record<string, unknown>;

export type LogRecord = {
  level: LogLevel;
  message: string;
  error?: unknown;
  meta: LogMeta;
};

// Errors from another realm (vm contexts, Jest) fail `instanceof Error`; the stack keeps plain `{ name, message }` data out.
const isErrorLike = (
  value: unknown
): value is { name: string; message: string; stack?: string } =>
  value instanceof Error ||
  (typeof value === "object" &&
    value !== null &&
    "name" in value &&
    typeof value.name === "string" &&
    "message" in value &&
    typeof value.message === "string" &&
    "stack" in value &&
    typeof value.stack === "string");

// Thrown or returned non-Error objects, such as PostgREST's `{ code, message, details, hint }`.
const hasStringMessage = (
  value: unknown
): value is { message: string; code?: unknown } =>
  typeof value === "object" &&
  value !== null &&
  "message" in value &&
  typeof value.message === "string";

// Matches the depth of Sentry's linked errors.
const MAX_CAUSE_DEPTH = 5;

const causeLines = (error: object, depth: number): string[] => {
  if (depth >= MAX_CAUSE_DEPTH || !("cause" in error) || error.cause == null) {
    return [];
  }
  const { cause } = error;
  // Like Sentry's linked errors, never serialize a non-Error cause: it can be a raw provider payload.
  if (!isErrorLike(cause)) {
    return [
      `Caused by: ${typeof cause === "string" ? cause : `[non-error ${typeof cause}]`}`,
    ];
  }
  return [
    `Caused by: ${cause.stack ?? `${cause.name}: ${cause.message}`}`,
    ...causeLines(cause, depth + 1),
  ];
};

// Datadog's standard error attributes; the cause chain is appended to the stack.
export const normalizeError = (
  error: unknown
):
  | { kind: string; message: string; stack?: string; code?: string }
  | undefined => {
  if (isErrorLike(error)) {
    const causes = causeLines(error, 0);
    return {
      kind: error.name,
      message: error.message,
      stack:
        causes.length === 0
          ? error.stack
          : [error.stack ?? `${error.name}: ${error.message}`, ...causes].join(
              "\n"
            ),
    };
  }
  if (error == null) return undefined;
  if (hasStringMessage(error)) {
    return {
      kind: typeof error,
      message: error.message,
      ...(typeof error.code === "string" ? { code: error.code } : {}),
    };
  }
  return {
    kind: typeof error,
    message: typeof error === "string" ? error : "unknown",
  };
};

/** An Error for trackers that title events from `Error.message`; other values pass through. */
export const toError = (error: unknown): unknown =>
  !isErrorLike(error) && hasStringMessage(error)
    ? new Error(error.message)
    : error;

/** JSON.stringify replacer that writes errors at any depth as Datadog error attributes. */
export const normalizeNestedErrors = (_key: string, value: unknown): unknown =>
  isErrorLike(value) ? normalizeError(value) : value;

const levelPriority: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

// Plain objects merge into meta (later wins), the first Error becomes `error`,
// and anything else is appended to the message the way console.log would.
type LogMethod = (message: string, ...args: unknown[]) => void;

export type Logger = {
  debug: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  error: LogMethod;
  captureException: {
    (error: unknown, ...tags: TagValue[]): void;
    (error: unknown, attributes: LogMeta, ...tags: TagValue[]): void;
  };
  captureMessage: {
    (message: string, level: CaptureMessageLevel, ...tags: TagValue[]): void;
    (
      message: string,
      level: CaptureMessageLevel,
      attributes: LogMeta,
      ...tags: TagValue[]
    ): void;
  };
  child: (meta: LogMeta) => Logger;
};

const isPlainObject = (value: unknown): value is LogMeta => {
  if (typeof value !== "object" || value === null) return false;
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const formatArg = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "object" && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return "[unserializable]";
    }
  }
  return String(value);
};

type CollectedArgs = { text: string[]; error?: unknown; meta: LogMeta };

// An error passed bare or as `{ error }` fills the same slot; later errors are
// appended to the message so none are lost.
const withError = (acc: CollectedArgs, error: unknown): CollectedArgs =>
  acc.error === undefined
    ? { ...acc, error }
    : { ...acc, text: [...acc.text, formatArg(error)] };

const collectArgs = (
  message: string,
  args: readonly unknown[]
): { message: string; error?: unknown; meta: LogMeta } => {
  const { text, error, meta } = args.reduce<CollectedArgs>(
    (acc, arg) => {
      if (isPlainObject(arg)) {
        const { error: fieldError, ...fields } = arg;
        const merged = { ...acc, meta: { ...acc.meta, ...fields } };
        return fieldError === undefined
          ? merged
          : withError(merged, fieldError);
      }
      if (arg instanceof Error) return withError(acc, arg);
      return { ...acc, text: [...acc.text, formatArg(arg)] };
    },
    { text: [message], meta: {} }
  );
  return { message: text.join(" "), error, meta };
};

// Shared by every logger, like Sentry's captured flag: an error captured, rethrown
// and captured again by a caller is reported once.
const capturedErrors = new WeakSet<object>();

const isRepeatCapture = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  if (capturedErrors.has(error)) return true;
  capturedErrors.add(error);
  return false;
};

export const createLogger = ({
  write,
  capture,
  minimumLevel = "info",
  getContext = () => ({}),
  bindings = {},
}: {
  write: (record: LogRecord) => void;
  // An extra destination for captures, after the record is written.
  capture?: CaptureSink;
  minimumLevel?: LogLevel;
  // Ambient correlation fields, typically `getContextAttributes`.
  getContext?: () => Readonly<LogMeta>;
  bindings?: LogMeta;
}): Logger => {
  const emit = (level: LogLevel, rawMessage: string, args: unknown[]) => {
    if (levelPriority[level] < levelPriority[minimumLevel]) return;
    const { message, error, meta } = collectArgs(rawMessage, args);
    write({
      level,
      message,
      error,
      // Ambient < child bindings < call meta.
      meta: { ...getContext(), ...bindings, ...meta },
    });
  };

  return {
    debug: (message, ...args) => emit("debug", message, args),
    info: (message, ...args) => emit("info", message, args),
    warn: (message, ...args) => emit("warn", message, args),
    error: (message, ...args) => emit("error", message, args),
    captureException: (error: unknown, ...args: (LogMeta | TagValue)[]) => {
      if (isRepeatCapture(error)) return;
      const { attributes, tags } = splitCaptureArgs(args);
      const record: CaptureRecord = {
        kind: "exception",
        error,
        attributes,
        tags,
        context: { ...getContext(), ...bindings },
      };
      write({
        level: "error",
        message: CAPTURED_EXCEPTION_MESSAGE,
        error,
        meta: captureToLogMeta(record),
      });
      capture?.(record);
    },
    captureMessage: (
      message: string,
      level: CaptureMessageLevel,
      ...args: (LogMeta | TagValue)[]
    ) => {
      const { attributes, tags } = splitCaptureArgs(args);
      const record: CaptureRecord = {
        kind: "message",
        message,
        level,
        attributes,
        tags,
        context: { ...getContext(), ...bindings },
      };
      if (levelPriority[level] >= levelPriority[minimumLevel]) {
        write({ level, message, meta: captureToLogMeta(record) });
      }
      capture?.(record);
    },
    child: (childMeta) =>
      createLogger({
        write,
        capture,
        minimumLevel,
        getContext,
        bindings: { ...bindings, ...childMeta },
      }),
  };
};
